/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
/**
 * devTools — client-side API + IndexedDB helpers for the Super-Admin
 * Configuration Dashboard.
 *
 * Every export is a no-op unless the build-time flag
 * ``REACT_APP_ENABLE_DEV_TOOLS`` is truthy — the caller can still import
 * them without breaking a production build where the flag is unset.
 */
import api from './api';

export const DEV_TOOLS_ENABLED = String(process.env.REACT_APP_ENABLE_DEV_TOOLS || '').toLowerCase() === 'true';

export const isDevToolsEnabled = () => DEV_TOOLS_ENABLED;

// ---------------------------------------------------------------------------
// Backend endpoints
// ---------------------------------------------------------------------------
export async function fetchDevStatus() {
  return (await api.get('/dev/status')).data;
}

export async function impersonateRole({ role, user_id } = {}) {
  return (await api.post('/dev/impersonate', { role, user_id })).data;
}

export async function seedDummyData({ patients = 10, alerts = 5, prescriptions = 10 } = {}) {
  return (await api.post('/dev/seed', { patients, alerts, prescriptions })).data;
}

export async function purgeDummyData() {
  return (await api.post('/dev/purge-seed')).data;
}

export async function simulateStripe({
  base_price_eur,
  family_discount_pct = 0,
  premium_discount_pct = 0,
  platform_fee_pct = 15,
  outcome = 'success',
}) {
  return (await api.post('/dev/stripe-simulator', {
    base_price_eur, family_discount_pct, premium_discount_pct, platform_fee_pct, outcome,
  })).data;
}

export async function mockSmtp({ to, subject, body }) {
  return (await api.post('/dev/smtp-mock', { to, subject, body })).data;
}

export async function fetchSmtpLog(limit = 20) {
  return (await api.get(`/dev/smtp-log?limit=${limit}`)).data;
}

// ---------------------------------------------------------------------------
// White-label toggle (persisted in localStorage, mirrors REACT_APP_MODE)
// ---------------------------------------------------------------------------
const WHITELABEL_STORAGE_KEY = 'sanitanexus.dev.whitelabel_override';

export const getWhitelabelOverride = () => {
  try { return localStorage.getItem(WHITELABEL_STORAGE_KEY) || 'standard'; }
  catch { return 'standard'; }
};

export const setWhitelabelOverride = (mode) => {
  try {
    if (mode === 'whitelabel') localStorage.setItem(WHITELABEL_STORAGE_KEY, 'whitelabel');
    else localStorage.removeItem(WHITELABEL_STORAGE_KEY);
  } catch { /* localStorage may be unavailable */ }
};

// ---------------------------------------------------------------------------
// IndexedDB seed — populates a local read-only store with fake records so
// the offline/whitelabel flow can be exercised without hitting the backend.
// ---------------------------------------------------------------------------
const IDB_NAME = 'sanitanexus_dev_seed';
const IDB_VERSION = 1;
const STORES = ['patients', 'alerts', 'prescriptions'];

const openDb = () => new Promise((resolve, reject) => {
  try {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      for (const s of STORES) {
        if (!db.objectStoreNames.contains(s)) {
          db.createObjectStore(s, { keyPath: 'id' });
        }
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  } catch (e) { reject(e); }
});

const writeAll = async (store, items) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    const s = tx.objectStore(store);
    for (const item of items) s.put(item);
    tx.oncomplete = () => resolve(items.length);
    tx.onerror = () => reject(tx.error);
  });
};

const clearAll = async (store) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).clear();
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
};

const countAll = async (store) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};

export async function seedLocalIndexedDB() {
  const now = new Date();
  const patients = Array.from({ length: 10 }, (_, i) => ({
    id: `idb-patient-${i + 1}`,
    full_name: ['Mario Rossi', 'Anna Verdi', 'Luca Bianchi', 'Giulia Neri', 'Paolo Ferrari',
                 'Sara Romano', 'Roberto Ricci', 'Chiara Marino', 'Fabio Greco', 'Elena Colombo'][i],
    birth_date: `19${60 + i}-0${(i % 9) + 1}-15`,
    sex: i % 2 === 0 ? 'M' : 'F',
    fiscal_code: `FRR${(i + 1).toString().padStart(3, '0')}A01H501Z`,
    created_at: now.toISOString(),
    __dev_local: true,
  }));
  const alerts = Array.from({ length: 5 }, (_, i) => ({
    id: `idb-alert-${i + 1}`,
    patient_id: patients[i % patients.length].id,
    type: ['blood_pressure_high', 'spo2_low', 'arrhythmia', 'fever', 'glucose_spike'][i],
    severity: i === 0 ? 'critical' : 'urgent',
    message: [
      'PA 180/110 mmHg — controllo immediato consigliato',
      'SpO2 88% — desaturazione notturna',
      'Aritmia sopraventricolare rilevata',
      'Temperatura 39.4°C persistente da 48h',
      'Glicemia 285 mg/dL post-prandiale',
    ][i],
    created_at: new Date(now.getTime() - i * 3600_000).toISOString(),
    __dev_local: true,
  }));
  const rxTemplates = [
    { drug: 'Amoxicillina', dose: '500 mg', freq: '3 volte/die', duration: 7 },
    { drug: 'Ibuprofene', dose: '400 mg', freq: 'al bisogno', duration: 5 },
    { drug: 'Ramipril', dose: '5 mg', freq: '1 volta/die', duration: 30 },
    { drug: 'Omeprazolo', dose: '20 mg', freq: '1 volta/die', duration: 14 },
    { drug: 'Metformina', dose: '500 mg', freq: '2 volte/die', duration: 90 },
    { drug: 'Atorvastatina', dose: '20 mg', freq: '1 volta/die', duration: 90 },
    { drug: 'Paracetamolo', dose: '1 g', freq: 'al bisogno', duration: 3 },
    { drug: 'Salbutamolo', dose: '100 mcg', freq: '2 puff', duration: 14 },
  ];
  const prescriptions = Array.from({ length: 12 }, (_, i) => {
    const tpl = rxTemplates[i % rxTemplates.length];
    return {
      id: `idb-rx-${i + 1}`,
      patient_id: patients[i % patients.length].id,
      drug_name: tpl.drug,
      dosage: tpl.dose,
      frequency: tpl.freq,
      duration_days: tpl.duration,
      created_at: new Date(now.getTime() - i * 24 * 3600_000).toISOString(),
      __dev_local: true,
    };
  });

  const [pCount, aCount, rCount] = await Promise.all([
    writeAll('patients', patients),
    writeAll('alerts', alerts),
    writeAll('prescriptions', prescriptions),
  ]);
  return { patients: pCount, alerts: aCount, prescriptions: rCount };
}

export async function clearLocalIndexedDB() {
  await Promise.all(STORES.map((s) => clearAll(s)));
  return { cleared: STORES };
}

export async function countLocalIndexedDB() {
  const [p, a, r] = await Promise.all([
    countAll('patients'),
    countAll('alerts'),
    countAll('prescriptions'),
  ]);
  return { patients: p, alerts: a, prescriptions: r };
}

// ---------------------------------------------------------------------------
// Impersonation banner state (mirrored in sessionStorage so the banner
// survives page reloads without a second /auth/me round-trip)
// ---------------------------------------------------------------------------
const IMPERSONATION_KEY = 'sanitanexus.dev.impersonation';

export const rememberImpersonation = (payload) => {
  try { sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(payload)); }
  catch { /* ignore */ }
};

export const clearImpersonation = () => {
  try { sessionStorage.removeItem(IMPERSONATION_KEY); }
  catch { /* ignore */ }
};

export const getImpersonation = () => {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
